import { z } from 'zod';

/**
 * Display data mode options for the chart
 */
export const DISPLAY_DATA_OPTIONS = [
  { value: 'actual', label: 'Actual Data' },
  { value: 'normalized', label: 'Normalized Data' },
] as const;

export type DisplayDataMode = (typeof DISPLAY_DATA_OPTIONS)[number]['value'];

export const TOOLTIP_FONT_SIZES = [
  { value: '0.75rem', label: 'Small' },
  { value: '1rem', label: 'Medium' },
  { value: '1.25rem', label: 'Large' },
] as const;

export type TooltipFontSize = (typeof TOOLTIP_FONT_SIZES)[number]['value'];

export const DEFAULT_TOOLTIP_FONT_SIZE: TooltipFontSize = '1rem';

/**
 * Schema for axis min/max configuration
 */
const YAxisMinMaxSchema = z
  .object({
    min: z.coerce
      .number()
      .nullable()
      .optional()
      .refine((val) => val === null || val === undefined || !isNaN(val), {
        message: 'Minimum must be a valid number',
      }),
    max: z.coerce
      .number()
      .nullable()
      .optional()
      .refine((val) => val === null || val === undefined || !isNaN(val), {
        message: 'Maximum must be a valid number',
      }),
  })
  .refine(
    (data) => {
      // If both min and max are provided, min must be less than max
      if (
        data.min !== null &&
        data.min !== undefined &&
        data.max !== null &&
        data.max !== undefined
      ) {
        return data.min < data.max;
      }
      return true;
    },
    {
      message: 'Minimum must be less than maximum',
      path: ['max'],
    }
  );

export type YAxisMinMax = z.infer<typeof YAxisMinMaxSchema>;

/**
 * Schema for chart settings form data
 */
export const ChartSettingsFormDataSchema = z.object({
  displayMode: z.enum(['actual', 'normalized']),
  yAxisMinMax: z.record(z.string(), YAxisMinMaxSchema).default({}),
  tooltipFontSize: z
    .enum(TOOLTIP_FONT_SIZES.map((size) => size.value) as [string, ...string[]])
    .default(DEFAULT_TOOLTIP_FONT_SIZE),
});

export type ChartSettingsFormData = z.infer<typeof ChartSettingsFormDataSchema>;

/**
 * Represents a visible axis with information about the first series associated with it.
 * Combines UnitGroup properties with optional first series color and symbol.
 */
export interface VisibleAxis {
  /** Axis index: 0-3 for visible axes */
  axisIndex: number;
  /** Axis position on chart */
  position: 'left' | 'right';
  /** Representative unit for this axis group */
  sourceUnit: string;
  /** Optional information about the first series on this axis */
  firstSeries?: {
    /** Color of the first series (hex string) */
    color: string;
    /** Symbol type of the first series (for mapping to Lucide icon) */
    symbol: string;
  };
}
